import React, { useContext, useEffect } from 'react';

import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRefinementList, useSortBy } from 'react-instantsearch';
import { useParams } from 'react-router-dom';

import { TenantCtx } from '../../../../containers/CommonLayout/CommonLayout';
import { DiscoverContext } from '../../../../context/DiscoverContext';
import useSERPNavigation from '../../../../hooks/useSERPNavigation';
import styled from '../../../../styled';
import { HITS_TYPES } from '../../../const';
import { FILTERS } from '../../const';
import { CategoryNavigationBlock } from '../CategoryNavigationBlock';
import { ExpertNavigationBlock } from '../ExpertNavigationBlock';
import { FilterWrapper } from '../FilterWrapper';
import { HierarchicalMenu as CustomHierarchicalMenu } from '../HierarchicalMenu';
import { IndexMenu } from '../IndexMenu';
import { RefinementList as CustomRefinementList } from '../RefinementList';
import RefinementListView from '../RefinementList/RefinementListView';

const CATEGORY_FILTER = [FILTERS.Category, FILTERS.Subcategory];

interface FiltersPanelProps {
  isMobile: boolean;
  resetTypeFilter?: boolean;
  isSearching?: boolean;

  loading?: boolean;
  initialExpert?: string;
  initialCategory?: string;
}

const FiltersPanel: React.FC<FiltersPanelProps> = ({
  isMobile,
  resetTypeFilter = false,
  isSearching = false,
  loading = false,
  initialExpert,
  initialCategory,
}) => {
  const { t } = useTranslation();
  const params = useParams();
  const serpType = useSERPNavigation();
  const { tenant } = useContext(TenantCtx);
  const { setRefineSearchType } = useContext(DiscoverContext);

  const onCategoriesPage = !!params?.category;
  const onExpertPage = !!params?.expertSlug;
  const onInsightsPage = serpType === HITS_TYPES.NEWS;
  const onGlossaryPage = serpType === HITS_TYPES.GLOSSARY;
  const onPathwaysPage = serpType === HITS_TYPES.PATHWAY;

  const indexName = `${process.env.REACT_APP_ALGOLIA_INDEX_NAME}_${tenant?.name}`;
  const sortedTitleAscIndexName = `${process.env.REACT_APP_ALGOLIA_INDEX_NAME}_${tenant?.name}_title_asc`;
  const sortedPublishedAtDescIndexName = `${process.env.REACT_APP_ALGOLIA_INDEX_NAME}_${tenant?.name}_published_at_desc`;

  const { refine } = useSortBy({
    items: [
      { value: sortedTitleAscIndexName, label: 'Sort Title Asc' },
      { value: sortedPublishedAtDescIndexName, label: 'Sort Published at Desc' },
      { value: indexName || '', label: 'Default' },
    ],
  });

  const { refine: refineSearchType, items: searchTypeItems } = useRefinementList({
    attribute: FILTERS.SearchType,
  });

  useEffect(() => {
    if (onGlossaryPage && !isSearching) {
      refine(sortedTitleAscIndexName);
    } else if (onInsightsPage && !isSearching) {
      refine(sortedPublishedAtDescIndexName);
    } else {
      refine(indexName);
    }
  }, [
    isSearching,
    refine,
    onGlossaryPage,
    sortedTitleAscIndexName,
    onInsightsPage,
    sortedPublishedAtDescIndexName,
    indexName,
  ]);

  useEffect(() => {
    setRefineSearchType(() => refineSearchType);
  }, [refineSearchType, setRefineSearchType]);

  return (
    <Wrapper>
      {onGlossaryPage && (
        <FilterWrapper title={t('page.discover.index')} withoutToggle>
          <IndexMenu attribute={FILTERS.Letter} limit={50} loading={loading} />
        </FilterWrapper>
      )}
      {onExpertPage && (
        <FilterWrapper title={t('page.discover.experts')} withoutToggle>
          <ExpertNavigationBlock attribute={FILTERS.Expert} initialExpert={initialExpert} />
        </FilterWrapper>
      )}
      {!onInsightsPage && (
        <FilterWrapper title={t('page.discover.categories')} withoutToggle={onCategoriesPage}>
          {onCategoriesPage ? (
            <>
              <CategoryNavigationBlock
                attributes={CATEGORY_FILTER}
                loading={loading}
                initialCategory={initialCategory}
              />
            </>
          ) : (
            <CustomHierarchicalMenu
              attributes={CATEGORY_FILTER}
              loading={loading}
              loadingItems={8}
            />
          )}
        </FilterWrapper>
      )}
      {!(onInsightsPage || onGlossaryPage) && (
        <FilterWrapper title={t('page.discover.level')}>
          <CustomRefinementList attribute={FILTERS.Difficulty} loadingItems={3} loading={loading} />
        </FilterWrapper>
      )}
      {!(onExpertPage || onGlossaryPage || onPathwaysPage) && (
        <FilterWrapper title={t('page.discover.experts')}>
          <CustomRefinementList
            attribute={FILTERS.Expert}
            showMore
            searchable
            limit={5}
            showMoreLimit={50}
            delay={isMobile ? 500 : 0}
            loading={loading}
          />
        </FilterWrapper>
      )}
      {!(onInsightsPage || onGlossaryPage) && (
        <FilterWrapper title={t('page.discover.duration')}>
          <CustomRefinementList attribute={FILTERS.Duration} loadingItems={4} loading={loading} />
        </FilterWrapper>
      )}
      {!onPathwaysPage && !onInsightsPage && !onGlossaryPage && (
        <FilterWrapper title={t('page.discover.subtitles')}>
          <CustomRefinementList attribute={FILTERS.Subtitles} loading={loading} />
        </FilterWrapper>
      )}
      {resetTypeFilter && (
        <FilterWrapper title={t('page.discover.type')}>
          <RefinementListView
            attribute={FILTERS.SearchType}
            loadingItems={4}
            loading={loading}
            refine={refineSearchType}
            items={searchTypeItems}
            itemsToShowCount={searchTypeItems?.length}
          />
        </FilterWrapper>
      )}
    </Wrapper>
  );
};

export default FiltersPanel;

const Wrapper = styled(Box)`
  ${({ theme }) => theme.breakpoints.up('xl')} {
    width: 300px;
  }
`;
